import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import {
  SignedIn,
  SignedOut,
  RedirectToSignUp,
  SignIn,
  SignUp,
  useUser,
} from "@clerk/clerk-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";
import { ThemeProvider } from "./components/theme/theme-provider";
import Layout from "./components/layout/Layout";
import StoriesPage from "./pages/StoriesPage";
import StoryDetailPage from "./pages/StoryDetailPage";
import FAQPage from "./pages/FAQPage";
import PortfolioPage from "./pages/legal/PortfolioPage";
import PrivacyPage from "./pages/legal/PrivacyPage";
import RefundPage from "./pages/legal/RefundPage";
import TermsPage from "./pages/legal/TermsPage";
import DisclaimerPage from "./pages/legal/DisclaimerPage";
import CartInitializer from "./components/cart/CartInitializer";
import CustomStoriesPage from "./pages/CustomStoriesPage";
import CustomCaseIntakePage from "./pages/CustomCaseIntakePage";
import CustomCaseAccessPage from "./pages/CustomCaseAccessPage";
import CustomCaseComingSoonPage from "./pages/CustomCaseComingSoonPage";
import FastSpringModal from "./components/cart/FastSpringModal";
import SurveyPage from "./pages/SurveyPage";
import ClubDashboardPage from "./pages/club/ClubDashboardPage";
import ClubSettingsPage from "./pages/club/ClubSettingsPage";
import ClubUnlockPage from "./pages/club/ClubUnlockPage";
import ClubCaseAccessPage from "./pages/club/ClubCaseAccessPage";
import ClubPaidMemberLandingPreviewPage from "./pages/club/ClubPaidMemberLandingPreviewPage";
import ClubWelcomePage from "./pages/club/ClubWelcomePage";
import ClubJoinCheckoutPage from "./pages/club/ClubJoinCheckoutPage";
import ClubTrialClaimPage from "./pages/club/ClubTrialClaimPage";
import ClubSessionSetupPage from "./pages/club/ClubSessionSetupPage";
import ClubSessionManagePage from "./pages/club/ClubSessionManagePage";
import ClubPlayPage from "./pages/club/ClubPlayPage";
import ClubArchiveEpisodePage from "./pages/club/ClubArchiveEpisodePage";
import ClubCustomOrdersPage from "./pages/club/ClubCustomOrdersPage";
import { useAuthStatus } from "./components/auth/AuthProvider";
import { devError, devLog, getRecentDevLogs } from "./utils/devLog";
import { CUSTOM_CASE_COMING_SOON_ENABLED } from "./utils/customCase";

const AGENCY_PREVIEW_STORAGE_KEY = "devmode";

const resolveAgencyPreviewEnabled = () => {
  const isClubEnabled = import.meta.env.VITE_CLUB_ENABLED === "true";

  if (!isClubEnabled) return false;
  if (import.meta.env.DEV) return true;
  if (import.meta.env.PROD) return true;
  if (typeof window === "undefined") return false;

  const params = new URLSearchParams(window.location.search);
  if (params.get("devmode") === "1") {
    try {
      window.localStorage.setItem(AGENCY_PREVIEW_STORAGE_KEY, "1");
    } catch {
      // Ignore localStorage write failures and fall through to direct access for this visit.
    }

    params.delete("devmode");
    const nextSearch = params.toString();
    const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}${window.location.hash || ""}`;
    window.history.replaceState(null, "", nextUrl);
    return true;
  }

  try {
    return window.localStorage.getItem(AGENCY_PREVIEW_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminStories = lazy(() => import("./pages/admin/AdminStories"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const ClubAdminMembersPage = lazy(() => import("./pages/club/admin/ClubAdminMembersPage"));
const ClubAdminCasesPage = lazy(() => import("./pages/club/admin/ClubAdminCasesPage"));
const ClubAdminBatchesPage = lazy(() => import("./pages/club/admin/ClubAdminBatchesPage"));
const ClubAdminTrialEmailPage = lazy(() => import("./pages/club/admin/ClubAdminTrialEmailPage"));
const ClubAdminArchivePage = lazy(() => import("./pages/club/admin/ClubAdminArchivePage"));
const ClubAdminCaseGenPage = lazy(() => import("./pages/club/admin/ClubAdminCaseGenPage"));
const ClubAdminCustomCaseConversionPage = lazy(
  () => import("./pages/club/admin/ClubAdminCustomCaseConversionPage")
);
const ClubAdminCustomCaseOrphansPage = lazy(
  () => import("./pages/club/admin/ClubAdminCustomCaseOrphansPage")
);
const ClubAdminCustomCaseReviewPage = lazy(
  () => import("./pages/club/admin/ClubAdminCustomCaseReviewPage")
);
const ClubAdminCustomOrdersPage = lazy(
  () => import("./pages/club/admin/ClubAdminCustomOrdersPage")
);
const ClubAdminCasePreviewPage = lazy(() => import("./pages/club/admin/ClubAdminCasePreviewPage"));
const ClubAdminEvidenceEditPage = lazy(() => import("./pages/club/admin/ClubAdminEvidenceEditPage"));
const ClubAdminCaseContentEditPage = lazy(() => import("./pages/club/admin/ClubAdminCaseContentEditPage"));
const ClubAdminSuspectEditPage = lazy(() => import("./pages/club/admin/ClubAdminSuspectEditPage"));
const ClubAdminProgressionPage = lazy(() => import("./pages/club/admin/ClubAdminProgressionPage"));
const ClubAdminPortraitLibraryPage = lazy(() => import("./pages/club/admin/ClubAdminPortraitLibraryPage"));
const AgencyCasePrintPage = lazy(() => import("./pages/club/AgencyCasePrintPage"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

class RouteErrorBoundary extends React.Component<
  { children: React.ReactNode; routeLabel?: string },
  { hasError: boolean; message: string }
> {
  constructor(props: { children: React.ReactNode; routeLabel?: string }) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(error: any) {
    return {
      hasError: true,
      message: String(error?.message || "Unexpected rendering error."),
    };
  }

  componentDidCatch(error: any, errorInfo: React.ErrorInfo) {
    devError("route", "render_failed", {
      route: this.props.routeLabel || "unknown",
      message: String(error?.message || ""),
      stack: String(error?.stack || ""),
      componentStack: String(errorInfo?.componentStack || ""),
    });
  }

  render() {
    if (this.state.hasError) {
      const recentLogs = import.meta.env.DEV ? getRecentDevLogs().slice(-12) : [];
      return (
        <section className="mx-auto max-w-2xl px-4 py-10 text-white">
          <div className="rounded-2xl border border-rose-300/25 bg-rose-950/30 p-6">
            <p className="text-xs uppercase tracking-[0.2em] text-rose-200">Render Error</p>
            <p className="mt-2 text-sm text-rose-100">
              {this.props.routeLabel ? `${this.props.routeLabel}: ` : ""}
              {this.state.message}
            </p>
            {import.meta.env.DEV ? (
              <>
                <p className="mt-4 text-xs uppercase tracking-[0.18em] text-rose-200/75">Dev Logs</p>
                <pre className="mt-2 max-h-80 overflow-auto rounded-xl border border-white/10 bg-black/30 p-3 text-[11px] leading-5 text-rose-50/90">
                  {recentLogs.length > 0
                    ? recentLogs
                        .map((entry) => `${entry.ts} ${entry.channel} ${entry.message} ${JSON.stringify(entry.data)}`)
                        .join("\n")
                    : "No dev logs captured yet. Open the browser console and filter for [ColdClientDebug]."}
                </pre>
              </>
            ) : null}
          </div>
        </section>
      );
    }
    return this.props.children;
  }
}

function App() {
  const isClubEnabled = import.meta.env.VITE_CLUB_ENABLED === "true";
  const [agencyPreviewEnabled, setAgencyPreviewEnabled] = React.useState(resolveAgencyPreviewEnabled);
  const location = useLocation();

  const isAgencyAccessible = isClubEnabled && agencyPreviewEnabled;

  React.useEffect(() => {
    devLog("route", "navigation", {
      path: location.pathname,
      search: location.search,
      hash: location.hash,
      isClubEnabled,
    });
  }, [location.pathname, location.search, location.hash, isClubEnabled]);

  React.useEffect(() => {
    const nextPreviewEnabled = resolveAgencyPreviewEnabled();
    setAgencyPreviewEnabled(nextPreviewEnabled);

    devLog("route", "agency_preview_visibility_resolved", {
      path: location.pathname,
      search: location.search,
      isClubEnabled,
      agencyPreviewEnabled: nextPreviewEnabled,
      isAgencyAccessible: isClubEnabled && nextPreviewEnabled,
      resolutionMode: import.meta.env.DEV ? "dev_always_on" : import.meta.env.PROD ? "prod_club_enabled" : "preview_flag",
    });
  }, [isClubEnabled, location.pathname, location.search]);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="dark" storageKey="cold-case-theme">
        <CartInitializer />
        <FastSpringModal />
        <Layout>
          <Routes>
            {/* Auth routes */}
            <Route
              path="/agency/sign-in/*"
              element={isAgencyAccessible ? <AgencySignInPage /> : <AgencyPreviewRedirect />}
            />
            <Route
              path="/agency/sign-up/*"
              element={isAgencyAccessible ? <AgencySignUpPage /> : <AgencyPreviewRedirect />}
            />
            <Route
              path="/sign-in/*"
              element={
                isAgencyAccessible ? (
                  <LegacyAuthRedirect fromPrefix="/sign-in" toPrefix="/agency/sign-in" />
                ) : (
                  <AgencyPreviewRedirect />
                )
              }
            />
            <Route
              path="/sign-up/*"
              element={
                isAgencyAccessible ? (
                  <LegacyAuthRedirect fromPrefix="/sign-up" toPrefix="/agency/sign-up" />
                ) : (
                  <AgencyPreviewRedirect />
                )
              }
            />

            {/* Redirect root to stories */}
            <Route path="/" element={<Navigate to="/stories" replace />} />
            <Route path="/stories" element={<StoriesPage />} />
            <Route path="/stories/:id" element={<StoryDetailPage />} />
            <Route
              path="/custom/intake/:accessToken"
              element={
                <CustomCaseAvailabilityGate>
                  <CustomCaseIntakePage />
                </CustomCaseAvailabilityGate>
              }
            />
            <Route
              path="/custom/case/:accessToken/evidence/:evidenceId"
              element={
                <CustomCaseAvailabilityGate>
                  <CustomCaseAccessPage />
                </CustomCaseAvailabilityGate>
              }
            />
            <Route
              path="/custom/case/:accessToken"
              element={
                <CustomCaseAvailabilityGate>
                  <CustomCaseAccessPage />
                </CustomCaseAvailabilityGate>
              }
            />
            <Route
              path="/custom/share/:shareToken/evidence/:evidenceId"
              element={
                <CustomCaseAvailabilityGate>
                  <CustomCaseAccessPage />
                </CustomCaseAvailabilityGate>
              }
            />
            <Route
              path="/custom/share/:shareToken"
              element={
                <CustomCaseAvailabilityGate>
                  <CustomCaseAccessPage />
                </CustomCaseAvailabilityGate>
              }
            />
            <Route
              path="/custom"
              element={
                <CustomCaseAvailabilityGate>
                  <CustomStoriesPage />
                </CustomCaseAvailabilityGate>
              }
            />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/survey" element={<SurveyPage />} />
            <Route
              path="/play/:token"
              element={
                <RouteErrorBoundary routeLabel="/play/:token">
                  <ClubPlayPage />
                </RouteErrorBoundary>
              }
            />
            <Route
              path="/play/:token/"
              element={
                <RouteErrorBoundary routeLabel="/play/:token/">
                  <ClubPlayPage />
                </RouteErrorBoundary>
              }
            />
            <Route
              path="/play/:token/evidence/:evidenceId"
              element={
                <RouteErrorBoundary routeLabel="/play/:token/evidence/:evidenceId">
                  <ClubPlayPage />
                </RouteErrorBoundary>
              }
            />
            <Route
              path="/play/:token/evidence/:evidenceId/"
              element={
                <RouteErrorBoundary routeLabel="/play/:token/evidence/:evidenceId/">
                  <ClubPlayPage />
                </RouteErrorBoundary>
              }
            />
            <Route
              path="/agency/member-preview"
              element={isAgencyAccessible ? <ClubPaidMemberLandingPreviewPage /> : <AgencyPreviewRedirect />}
            />

            {isAgencyAccessible && (
              <>
                <Route
                  path="/agency"
                  element={<AgencyEntryPage />}
                />
                <Route
                  path="/agency/index.html"
                  element={<AgencyEntryPage />}
                />
                <Route
                  path="/agency/dashboard"
                  element={
                    <ProtectedRoute>
                      <ClubDashboardPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/agency/custom-cases"
                  element={
                    <ProtectedRoute>
                      <ClubCustomOrdersPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/agency/sessions/new"
                  element={
                    <ProtectedRoute>
                      <ClubSessionSetupPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/agency/sessions/manage"
                  element={
                    <ProtectedRoute>
                      <ClubSessionManagePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/agency/settings"
                  element={
                    <ProtectedRoute>
                      <ClubSettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/agency/unlock"
                  element={
                    <ProtectedRoute>
                      <ClubUnlockPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/agency/cases/:caseIndex/:caseSlug?"
                  element={
                    <ProtectedRoute>
                      <ClubCaseAccessPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/agency/archive/:archiveNumber/episodes/:episodeNumber/:episodeSlug?"
                  element={
                    <ProtectedRoute>
                      <ClubArchiveEpisodePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/agency/cases/:caseIndex/:caseSlug?/evidence/:evidenceId"
                  element={
                    <ProtectedRoute>
                      <LazyPage>
                        <ClubAdminCasePreviewPage />
                      </LazyPage>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/agency/cases/:caseIndex/:caseSlug?/final"
                  element={
                    <ProtectedRoute>
                      <LazyPage>
                        <ClubAdminCasePreviewPage />
                      </LazyPage>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/agency/cases/:caseIndex/:caseSlug?/print"
                  element={
                    <ProtectedRoute>
                      <LazyPage>
                        <AgencyCasePrintPage />
                      </LazyPage>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/agency/join"
                  element={
                    <ProtectedRoute>
                      <ClubJoinCheckoutPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/agency/trial"
                  element={
                    <ProtectedRoute>
                      <ClubTrialClaimPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/agency/admin/members"
                  element={
                    <ClubAdminRoute>
                      <LazyPage>
                        <ClubAdminMembersPage />
                      </LazyPage>
                    </ClubAdminRoute>
                  }
                />
                <Route
                  path="/agency/admin/progression"
                  element={
                    <ClubAdminRoute>
                      <LazyPage>
                        <ClubAdminProgressionPage />
                      </LazyPage>
                    </ClubAdminRoute>
                  }
                />
                <Route
                  path="/agency/admin/archive"
                  element={
                    <ClubAdminRoute>
                      <LazyPage>
                        <ClubAdminArchivePage />
                      </LazyPage>
                    </ClubAdminRoute>
                  }
                />
                <Route
                  path="/agency/admin/portraits"
                  element={
                    <ClubAdminRoute>
                      <LazyPage>
                        <ClubAdminPortraitLibraryPage />
                      </LazyPage>
                    </ClubAdminRoute>
                  }
                />
                <Route
                  path="/agency/admin/cases"
                  element={
                    <ClubAdminRoute>
                      <RouteErrorBoundary routeLabel="/agency/admin/cases">
                        <LazyPage>
                          <ClubAdminCasesPage />
                        </LazyPage>
                      </RouteErrorBoundary>
                    </ClubAdminRoute>
                  }
                />
                <Route
                  path="/agency/admin/cases/:id/preview"
                  element={
                    <ClubAdminRoute>
                      <LazyPage>
                        <ClubAdminCasePreviewPage />
                      </LazyPage>
                    </ClubAdminRoute>
                  }
                />
                <Route
                  path="/agency/admin/cases/:id/preview/evidence/:evidenceId"
                  element={
                    <ClubAdminRoute>
                      <LazyPage>
                        <ClubAdminCasePreviewPage />
                      </LazyPage>
                    </ClubAdminRoute>
                  }
                />
                <Route
                  path="/agency/admin/cases/:id/final"
                  element={
                    <ClubAdminRoute>
                      <LazyPage>
                        <ClubAdminCasePreviewPage />
                      </LazyPage>
                    </ClubAdminRoute>
                  }
                />
                <Route
                  path="/agency/admin/cases/:id/final/evidence/:evidenceId"
                  element={
                    <ClubAdminRoute>
                      <LazyPage>
                        <ClubAdminCasePreviewPage />
                      </LazyPage>
                    </ClubAdminRoute>
                  }
                />
                <Route
                  path="/agency/admin/cases/:id/print"
                  element={
                    <ClubAdminRoute>
                      <LazyPage>
                        <AgencyCasePrintPage />
                      </LazyPage>
                    </ClubAdminRoute>
                  }
                />
                <Route
                  path="/agency/admin/cases/:id/evidence/:evidenceId/edit"
                  element={
                    <ClubAdminRoute>
                      <LazyPage>
                        <ClubAdminEvidenceEditPage />
                      </LazyPage>
                    </ClubAdminRoute>
                  }
                />
                <Route
                  path="/agency/admin/cases/:id/content/edit"
                  element={
                    <ClubAdminRoute>
                      <LazyPage>
                        <ClubAdminCaseContentEditPage />
                      </LazyPage>
                    </ClubAdminRoute>
                  }
                />
                <Route
                  path="/agency/admin/cases/:id/suspect/:suspectIndex/edit"
                  element={
                    <ClubAdminRoute>
                      <LazyPage>
                        <ClubAdminSuspectEditPage />
                      </LazyPage>
                    </ClubAdminRoute>
                  }
                />
                <Route
                  path="/agency/admin/cases/:id/suspect/:suspectIndex/final"
                  element={
                    <ClubAdminRoute>
                      <LazyPage>
                        <ClubAdminSuspectEditPage />
                      </LazyPage>
                    </ClubAdminRoute>
                  }
                />
                <Route
                  path="/agency/admin/batches"
                  element={
                    <ClubAdminRoute>
                      <LazyPage>
                        <ClubAdminBatchesPage />
                      </LazyPage>
                    </ClubAdminRoute>
                  }
                />
                <Route
                  path="/agency/admin/email-ops"
                  element={
                    <ClubAdminRoute>
                      <LazyPage>
                        <ClubAdminTrialEmailPage />
                      </LazyPage>
                    </ClubAdminRoute>
                  }
                />
                <Route
                  path="/agency/admin/trial-email"
                  element={
                    <ClubAdminRoute>
                      <LazyPage>
                        <ClubAdminTrialEmailPage />
                      </LazyPage>
                    </ClubAdminRoute>
                  }
                />
                <Route
                  path="/agency/admin/casegen"
                  element={
                    <ClubAdminRoute>
                      <LazyPage>
                        <ClubAdminCaseGenPage />
                      </LazyPage>
                    </ClubAdminRoute>
                  }
                />
                <Route
                  path="/agency/admin/custom-case-reviews"
                  element={
                    <ClubAdminRoute>
                      <LazyPage>
                        <ClubAdminCustomCaseReviewPage />
                      </LazyPage>
                    </ClubAdminRoute>
                  }
                />
                <Route
                  path="/agency/admin/custom-orders"
                  element={
                    <ClubAdminRoute>
                      <LazyPage>
                        <ClubAdminCustomOrdersPage />
                      </LazyPage>
                    </ClubAdminRoute>
                  }
                />
                <Route
                  path="/agency/admin/custom-case-conversion"
                  element={
                    <ClubAdminRoute>
                      <LazyPage>
                        <ClubAdminCustomCaseConversionPage />
                      </LazyPage>
                    </ClubAdminRoute>
                  }
                />
                <Route
                  path="/agency/admin/custom-case-orphans"
                  element={
                    <ClubAdminRoute>
                      <LazyPage>
                        <ClubAdminCustomCaseOrphansPage />
                      </LazyPage>
                    </ClubAdminRoute>
                  }
                />
                <Route path="/club/*" element={<LegacyClubRedirect />} />
              </>
            )}

            {!isAgencyAccessible && (
              <>
                <Route path="/agency/*" element={<AgencyPreviewRedirect />} />
                <Route path="/club/*" element={<AgencyPreviewRedirect />} />
              </>
            )}

            {/* Legal and Info Pages */}

            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/refund" element={<RefundPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/disclaimer" element={<DisclaimerPage />} />
            <Route path="/portfolio" element={<PortfolioPage />} />

            {/* Protected routes */}
            <Route
              path="/account"
              element={
                <ProtectedRoute>
                  <Navigate to="/agency/settings" replace />
                </ProtectedRoute>
              }
            />

            {/* Admin routes */}
            <Route
              path="/admin"
              element={
                <ClubAdminRoute>
                  <LazyPage>
                    <AdminDashboard />
                  </LazyPage>
                </ClubAdminRoute>
              }
            />
            <Route
              path="/admin/stories"
              element={
                <ClubAdminRoute>
                  <LazyPage>
                    <AdminStories />
                  </LazyPage>
                </ClubAdminRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ClubAdminRoute>
                  <LazyPage>
                    <AdminUsers />
                  </LazyPage>
                </ClubAdminRoute>
              }
            />
            <Route
              path="/admin/members"
              element={
                <ClubAdminRoute>
                  <LazyPage>
                    <ClubAdminMembersPage />
                  </LazyPage>
                </ClubAdminRoute>
              }
            />
            <Route
              path="/admin/progression"
              element={
                <ClubAdminRoute>
                  <LazyPage>
                    <ClubAdminProgressionPage />
                  </LazyPage>
                </ClubAdminRoute>
              }
            />
            <Route
              path="/admin/archive"
              element={
                <ClubAdminRoute>
                  <LazyPage>
                    <ClubAdminArchivePage />
                  </LazyPage>
                </ClubAdminRoute>
              }
            />
            <Route
              path="/admin/portraits"
              element={
                <ClubAdminRoute>
                  <LazyPage>
                    <ClubAdminPortraitLibraryPage />
                  </LazyPage>
                </ClubAdminRoute>
              }
            />
            <Route
              path="/admin/cases"
              element={
                <ClubAdminRoute>
                  <LazyPage>
                    <ClubAdminCasesPage />
                  </LazyPage>
                </ClubAdminRoute>
              }
            />
            <Route
              path="/admin/custom-case-reviews"
              element={
                <ClubAdminRoute>
                  <LazyPage>
                    <ClubAdminCustomCaseReviewPage />
                  </LazyPage>
                </ClubAdminRoute>
              }
            />
            <Route
              path="/admin/custom-orders"
              element={
                <ClubAdminRoute>
                  <LazyPage>
                    <ClubAdminCustomOrdersPage />
                  </LazyPage>
                </ClubAdminRoute>
              }
            />
            <Route
              path="/admin/custom-case-conversion"
              element={
                <ClubAdminRoute>
                  <LazyPage>
                    <ClubAdminCustomCaseConversionPage />
                  </LazyPage>
                </ClubAdminRoute>
              }
            />
            <Route
              path="/admin/custom-case-orphans"
              element={
                <ClubAdminRoute>
                  <LazyPage>
                    <ClubAdminCustomCaseOrphansPage />
                  </LazyPage>
                </ClubAdminRoute>
              }
            />
            <Route
              path="/admin/batches"
              element={
                <ClubAdminRoute>
                  <LazyPage>
                    <ClubAdminBatchesPage />
                  </LazyPage>
                </ClubAdminRoute>
              }
            />
            <Route
              path="/admin/email-ops"
              element={
                <ClubAdminRoute>
                  <LazyPage>
                    <ClubAdminTrialEmailPage />
                  </LazyPage>
                </ClubAdminRoute>
              }
            />
            <Route
              path="/admin/trial-email"
              element={
                <ClubAdminRoute>
                  <LazyPage>
                    <ClubAdminTrialEmailPage />
                  </LazyPage>
                </ClubAdminRoute>
              }
            />
            <Route
              path="/admin/analytics"
              element={
                <ClubAdminRoute>
                  <LazyPage>
                    <AdminAnalytics />
                  </LazyPage>
                </ClubAdminRoute>
              }
            />
            <Route
              path="*"
              element={
                <section className="mx-auto max-w-2xl px-4 py-10 text-white">
                  <div className="rounded-2xl border border-rose-300/25 bg-rose-950/30 p-6">
                    <p className="text-xs uppercase tracking-[0.2em] text-rose-200">Route not found</p>
                    <p className="mt-2 text-sm text-rose-100">
                      This link does not match an available page in this build.
                    </p>
                  </div>
                </section>
              }
            />
          </Routes>
        </Layout>
        <Toaster position="top-right" />
      </ThemeProvider>
    </QueryClientProvider>
  );
}

// Protected route wrapper
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  return (
    <>
      <SignedIn>{children}</SignedIn>
      <SignedOut>
        <RedirectToSignUp />
      </SignedOut>
    </>
  );
};

const ClubAdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAdmin, isLoading } = useAuthStatus();

  return (
    <ProtectedRoute>
      {isLoading ? (
        <div className="p-6 text-white">Checking admin access...</div>
      ) : isAdmin ? (
        children
      ) : (
        <Navigate to="/agency/dashboard" replace />
      )}
    </ProtectedRoute>
  );
};

const CustomCaseAvailabilityGate = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();

  React.useEffect(() => {
    devLog("route", "custom_case_availability_gate_resolved", {
      path: location.pathname,
      search: location.search,
      hash: location.hash,
      comingSoonEnabled: CUSTOM_CASE_COMING_SOON_ENABLED,
      resolution: CUSTOM_CASE_COMING_SOON_ENABLED ? "coming_soon_page" : "custom_case_experience",
    });
  }, [location.hash, location.pathname, location.search]);

  if (CUSTOM_CASE_COMING_SOON_ENABLED) {
    return <CustomCaseComingSoonPage />;
  }

  return <>{children}</>;
};

const AgencyPreviewRedirect = () => {
  const location = useLocation();

  React.useEffect(() => {
    devLog("route", "agency_preview_redirected", {
      path: location.pathname,
      search: location.search,
      hash: location.hash,
    });
  }, [location.hash, location.pathname, location.search]);

  return <Navigate to="/stories" replace />;
};

const LegacyClubRedirect = () => {
  const location = useLocation();
  const agencyPath = location.pathname.replace(/^\/club(?=\/|$)/, "/agency");
  return <Navigate to={`${agencyPath}${location.search}${location.hash}`} replace />;
};

const LegacyAuthRedirect = ({ fromPrefix, toPrefix }: { fromPrefix: string; toPrefix: string }) => {
  const location = useLocation();
  const fromPath = String(fromPrefix || "").trim();
  const toPath = String(toPrefix || "").trim();

  if (!fromPath || !toPath) return <Navigate to="/agency" replace />;

  const mappedPath =
    location.pathname === fromPath || location.pathname.startsWith(`${fromPath}/`)
      ? `${toPath}${location.pathname.slice(fromPath.length)}`
      : toPath;

  return <Navigate to={`${mappedPath}${location.search}${location.hash}`} replace />;
};

const AgencyEntryPage = () => {
  const { isSignedIn, isLoaded } = useUser();

  React.useEffect(() => {
    devLog("auth", "agency_entry_state", {
      isLoaded,
      isSignedIn,
    });
  }, [isLoaded, isSignedIn]);

  // Never block route rendering on auth-bootstrap state; if sign-in is confirmed, redirect.
  if (isSignedIn === true) {
    return <Navigate to="/agency/dashboard" replace />;
  }

  return <ClubWelcomePage onMembershipCreated={() => {}} />;
};

const AgencyAuthShell = ({ children }: { children: React.ReactNode }) => (
  <section className="min-h-screen w-full bg-[#05070c] px-4 py-10 text-[#eceae4]">
    <div className="mx-auto max-w-5xl">{children}</div>
  </section>
);

const AgencySignInPage = () => (
  <AgencyAuthShell>
    <AgencySignInPanel />
  </AgencyAuthShell>
);

const AgencySignUpPage = () => (
  <AgencyAuthShell>
    <AgencySignUpPanel />
  </AgencyAuthShell>
);

const AgencySignInPanel = () => {
  const { isSignedIn, isLoaded } = useUser();

  React.useEffect(() => {
    devLog("auth", "sign_in_route_render", {
      isLoaded,
      isSignedIn,
    });
  }, [isLoaded, isSignedIn]);

  return (
    <SignIn
      routing="path"
      path="/agency/sign-in"
      signUpUrl="/agency/sign-up"
      forceRedirectUrl="/agency/dashboard"
      fallbackRedirectUrl="/agency/dashboard"
      signUpForceRedirectUrl="/agency/dashboard"
    />
  );
};

const AgencySignUpPanel = () => {
  const { isSignedIn, isLoaded } = useUser();

  React.useEffect(() => {
    devLog("auth", "sign_up_route_render", {
      isLoaded,
      isSignedIn,
    });
  }, [isLoaded, isSignedIn]);

  return (
    <SignUp
      routing="path"
      path="/agency/sign-up"
      signInUrl="/agency/sign-in"
      fallbackRedirectUrl="/agency/dashboard"
      signInForceRedirectUrl="/agency/dashboard"
    />
  );
};

const LazyPage = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<div className="p-6 text-white">Loading page...</div>}>{children}</Suspense>
);

export default App;
